// Sunset Score and Sunrise Score: the next sunset or sunrise rated 0-10 by Sunsethue, or, when it has nothing for
// the day, by our rule of thumb from the cloud layers (config/sky.json), with the one after alongside. When each airs
// is rules.mjs's call (Oct 27: Sunrise from midnight until sunrise, Sunset from noon until sunset); the footer names
// the source.
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import { getTimes } from '../../vendor/auto/suncalc.js';
import SnapshotDisplay from './snapshot-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import {
	asOf, time, weekday, dayKey, isToday,
} from './format.mjs';

const { sky: rules, water } = window.RHL;
const DAY = 86400000;
const HOUR = 3600000;
const AFTER = 20 * 60000;

// the horizon whose low cloud blocks the light: offshore for sunset, the East Bay for sunrise
const EVENTS = {
	sunset: {
		name: 'sunset', far: 'lowWest', farLabel: 'Low, offshore', today: 'Tonight', title: 'Sunset Score',
	},
	sunrise: {
		name: 'sunrise', far: 'lowEast', farLabel: 'Low, East Bay', today: 'This morning', title: 'Sunrise Score',
	},
};

const bell = (value, peak) => Math.max(0, 1 - Math.abs(value - peak) / peak);

// the event on the calendar day of `date` in the channel's time zone; 20:00 UTC is midday on the Pacific coast
const eventOn = (type, date) => {
	const [y, m, d] = dayKey(date).split('-').map(Number);
	return getTimes(new Date(Date.UTC(y, m - 1, d, 20)), water.point.lat, water.point.lon)[type];
};

// cloud layers at the hour nearest a time
const layersAt = (sky, at, far) => {
	const i = sky.times.reduce((best, t, idx) => (Math.abs(new Date(t) - at) < Math.abs(new Date(sky.times[best]) - at) ? idx : best), 0);
	if (Math.abs(new Date(sky.times[i]) - at) > 2 * HOUR || sky[far] === undefined) return null;
	return {
		low: sky.low[i], mid: sky.mid[i], high: sky.high[i], far: sky[far][i],
	};
};

const score = (l) => {
	const raw = rules.base
		+ rules.high.points * bell(l.high, rules.high.peak)
		+ rules.mid.points * bell(l.mid, rules.mid.peak)
		- rules.lowHere.penalty * (l.low / 100)
		- rules.lowFar.penalty * (l.far / 100);
	return Math.round(Math.max(0, Math.min(10, raw)));
};
const word = (value, l) => {
	if (Math.max(l.low, l.far) >= rules.foggedIn) return 'Fogged in';
	return rules.words.find(([max]) => value <= max)[1];
};

// the next two events still to come (or under 20 minutes past), Sunsethue's score where it has the day
const upcoming = (event, now) => {
	const sky = data('sky');
	const hue = (data('sunsethue') ?? []).filter((h) => (h.type ?? 'sunset') === event.name);
	return [0, 1, 2].map((offset) => new Date(now.getTime() + offset * DAY)).map((day) => {
		const forecast = hue.find((h) => h.date === dayKey(day) && h.quality !== null && h.quality !== undefined);
		const at = forecast?.t ? new Date(forecast.t) : eventOn(event.name, day);
		const layers = sky ? layersAt(sky, at, event.far) : null;
		if (forecast) {
			return {
				at, layers, score: Math.round(forecast.quality * 10), word: forecast.text, golden: forecast.golden, source: 'sunsethue',
			};
		}
		return layers ? {
			at, layers, score: score(layers), word: word(score(layers), layers), source: 'ours',
		} : null;
	}).filter((e) => e && now - e.at < AFTER).slice(0, 2);
};

const dayLabel = (event, at, now) => {
	if (isToday(at, now)) return event.today;
	if (dayKey(at) === dayKey(now.getTime() + DAY)) return 'Tomorrow';
	return weekday(at);
};

const meter = (value) => `<div class="meter">${Array.from({ length: 10 }, (_, i) => `<i class="${i < value ? 'on sun' : ''}"></i>`).join('')}</div>`;

class SkyScore extends SnapshotDisplay {
	constructor(navId, elemId, event) {
		super(navId, elemId, event.title, { seconds: 18 });
		this.event = event;
	}

	hasData() {
		return upcoming(this.event, new Date()).length > 0;
	}

	render(now) {
		const { event } = this;
		const [next, after] = upcoming(event, now);
		const l = next.layers;
		const rows = [
			// golden hour leads into a sunset and follows a sunrise
			next.golden && (event.name === 'sunset' ? ['Golden from', time(next.golden[0])] : ['Golden until', time(next.golden[1])]),
			...(l ? [['High clouds', `${l.high}%`], ['Mid clouds', `${l.mid}%`], ['Low, here', `${l.low}%`], [event.farLabel, `${l.far}%`]] : []),
		].filter(Boolean);
		const credit = next.source === 'sunsethue'
			? `Sunsethue &middot; ${asOf(fetchedAt('sunsethue'), now)}`
			: `Our estimate from Open-Meteo clouds &middot; ${asOf(fetchedAt('sky'), now)}`;
		return `<div class="cols">
			<div>
				<div class="hdr">${dayLabel(event, next.at, now)} &middot; ${time(next.at)}</div>
				<div class="score-label">${event.name === 'sunset' ? 'Sunset' : 'Sunrise'} score</div>
				<div class="sunset-word">${escapeHtml(next.word)} &middot; ${next.score}/10</div>
				${meter(next.score)}
				${after ? `<div class="hdr gap">${dayLabel(event, after.at, now)} &middot; ${time(after.at)}</div><div>${escapeHtml(after.word)} &middot; ${after.score}/10 <span class="dim small">score</span></div>` : ''}
			</div>
			<div>
				<div class="hdr">At ${event.name}</div>
				<div class="kv">${rows.map(([k, v]) => `<div>${k}</div><div class="v">${v}</div>`).join('')}</div>
			</div>
		</div>
		<div class="asof">${credit}</div>`;
	}
}

registerDisplay(new SkyScore(26, 'sunset-score', EVENTS.sunset));
registerDisplay(new SkyScore(27, 'sunrise-score', EVENTS.sunrise));
